import { isGradient } from "@components/color-picker/lib/detect";

import { gradientBorderCss, safeGradient } from "@/shared/utils/gradient-css";
import { hasBorder } from "@library/shared";
import styled, { css } from "styled-components";

/**
 * Inner wrapper padding by set corner radius:
 * 0px, 8px → 16px; 16px → 20px; 32px → 24px
 */
function getSetInnerWrapperPaddingByRadius(borderRadius: string): string {
  const r = borderRadius?.trim() ?? "0";
  if (r === "16px") return "20px";
  if (r === "32px") return "24px";
  return "16px"; // 0px, 8px, or any other
}

type SetDesignedProps = {
  $groupsRatio: [string, string];
  $verticalAlignment: string;
  $horizontalAlignment: string;
  $backgroundColor: string;
  $borderColor: string;
  $borderWidth: number;
  $borderRadius: string;
};

export const SetStyled = styled.div<SetDesignedProps>`
  &.set {
    --set-inner-wrapper-padding-x: ${({ $borderRadius }) =>
      getSetInnerWrapperPaddingByRadius($borderRadius)};
    --set-inner-wrapper-padding-y: ${({ $borderRadius }) =>
      getSetInnerWrapperPaddingByRadius($borderRadius)};
    --mobile-set-inner-wrapper-padding-x: ${({ $borderRadius }) =>
      getSetInnerWrapperPaddingByRadius($borderRadius)};
    --mobile-set-inner-wrapper-padding-y: ${({ $borderRadius }) =>
      getSetInnerWrapperPaddingByRadius($borderRadius)};

    /* Published so descendants that flip the flex direction (and therefore
       swap which property carries which axis) can still read the author's
       intent — e.g. the split horizontal timeline's cards. */
    --set-vertical-alignment: ${({ $verticalAlignment }) =>
      $verticalAlignment || "center"};
    --set-horizontal-alignment: ${({ $horizontalAlignment }) =>
      $horizontalAlignment || "center"};

    justify-content: ${({ $horizontalAlignment }) => $horizontalAlignment};
    align-items: ${({ $verticalAlignment }) => $verticalAlignment};

    /* The global .set rule (set/styles/index.scss) forces flex-flow: column
       on mobile to stack groups vertically. In column flex, justify-content
       controls the MAIN (vertical) axis and align-items controls the CROSS
       (horizontal) axis — the inverse of row. Without this swap the editor's
       verticalAlignment ends up controlling horizontal positioning, and the
       editor's vertical "top / center / bottom" setting silently has no effect
       on vertical position. Most visible in combos where sets have real
       vertical room (arrays, horizontal timeline, tabs). */
    @media screen and (max-width: 768px) {
      justify-content: ${({ $verticalAlignment }) => $verticalAlignment};
      align-items: ${({ $horizontalAlignment }) => $horizontalAlignment};
    }

    ${({ $horizontalAlignment }) =>
      $horizontalAlignment === "start" &&
      `
            text-align:left;
        `};
    ${({ $horizontalAlignment }) =>
      $horizontalAlignment === "center" &&
      `
            text-align:center;
        `};
    ${({ $horizontalAlignment }) =>
      $horizontalAlignment === "end" &&
      `
            text-align:right;
        `};
    .setInnerWrapper {
      background: ${({ $backgroundColor }) => {
        if (!$backgroundColor) return "none";
        if (isGradient($backgroundColor)) {
          return safeGradient($backgroundColor) ?? "none";
        }
        return $backgroundColor;
      }};

      ${({ $borderColor, $borderWidth }) =>
        hasBorder({ borderColor: $borderColor, borderWidth: $borderWidth }) &&
        gradientBorderCss($borderColor, $borderWidth)};
      border-radius: ${({ $borderRadius }) =>
        $borderRadius ? $borderRadius : "0"};
      .emptyGroup.pos-0 {
        width: ${({ $groupsRatio }) => $groupsRatio[0] + "%"};
      }
      .emptyGroup.pos-1 {
        width: ${({ $groupsRatio }) => $groupsRatio[1] + "%"};
      }

      ${({ $groupsRatio }) =>
        $groupsRatio[0] === "100" &&
        $groupsRatio[1] === "100" &&
        css`
          flex-wrap: wrap;
          .group.pos-0 {
            padding-right: 0;
            margin-bottom: 8px;
          }
          .group.pos-1 {
            padding-left: 0;
          }
        `}
    }
    &.current {
      border-radius: 2px;
      outline: 2px solid #610cff !important;
    }
  }
`;
// It exists specifically for Image Array vertical alignment
export const GroupWrapper = styled.div`
  display: inherit;
  align-items: inherit;
  justify-content: inherit;
  flex-flow: inherit;
  width: inherit;
`;

export const SetDesignedEditor = styled(SetStyled)`
  &.set {
    &:hover {
      outline: 1px solid rgba(63, 162, 254, 1);
    }
    .emptyGroup {
      width: ${({ $groupsRatio }) =>
        $groupsRatio[0] + "%" || $groupsRatio[1] + "%"};
    }
  }
`;

export const ActionsWrapper = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  z-index: 2;
  width: 100%;
  min-width: max-content;
  right: 0;
  display: inline-flex;
  pointer-events: none;
  > .over {
    transform: translate(8px, calc(100% + 16px));
  }
`;
